import React from 'react';
import { FORMA_ETIQUETA, FORMA_ETIQUETA_GRANDE } from './EtiquetasIntervenciones';

/**
 * LA ETIQUETA DE LAS MARCAS DE PACIENTE (administración, 21/10/2026)
 * -----------------------------------------------------------------
 * La marca de paciente (services/marcas_paciente.py) se ve como una ETIQUETA
 * con la MISMA forma que las rojas «Cirugía — fecha» de las intervenciones
 * (EtiquetasIntervenciones.jsx): mayúsculas, negrita, letras blancas y
 * esquinas redondeadas, pero en el COLOR DE LA MARCA. Sale junto al nombre en
 * la ficha, en la agenda, en el resumen de la cita y en el parte del día (y en
 * el parte impreso, services/agenda_dia_pdf_generator.py).
 *
 *  · En la FICHA dice el nombre de la marca (`nombre`).
 *  · En la CITA dice la copia que guarda la cita (`marcas_paciente:
 *    [{id, texto, color}]`): el «Texto en las citas» de Configuración, que si
 *    va vacío es el nombre. Ya no va como una línea en las notas de
 *    administración: la etiqueta lo dice.
 *
 * Ni el texto ni el color están aquí: los pone la clínica en Configuración.
 */

// El de la marca sin color guardado (el mismo que services/marcas_paciente).
export const COLOR_MARCA_POR_DEFECTO = '#7C3AED';
const COLOR = /^#[0-9a-fA-F]{6}$/;

/** El color de la marca, o el de por defecto si no trae uno válido. */
export function colorDeMarca(color) {
  const c = String(color || '').trim();
  return COLOR.test(c) ? c : COLOR_MARCA_POR_DEFECTO;
}

/** Las marcas de una cita que se pueden pintar (con texto), en su orden. */
export function marcasDeLaCita(marcas) {
  return (Array.isArray(marcas) ? marcas : [])
    .filter((m) => m && String(m.texto || '').trim())
    .map((m) => ({ ...m, texto: String(m.texto).trim() }));
}

/** Lo que se lee al pasar el ratón por la etiqueta. */
export function tituloMarca(texto) {
  return `Marca de paciente: ${texto}`;
}

// Cómo se coloca: en su línea, y si el texto (que escribe la clínica) no cabe,
// se corta con «…» dentro de su sitio en vez de salirse; el título lo dice
// entero.
export const AJUSTE_ETIQUETA_MARCA = 'inline-block max-w-full truncate align-middle';

/**
 * Una etiqueta. `grande`: el tamaño de la cabecera de la ficha (el mismo que
 * sus etiquetas de intervenciones). `className` añade lo de cada sitio
 * (márgenes, cuánto puede encoger). `como`: la ficha la usa de botón.
 */
export function EtiquetaMarcaPaciente({
  texto, color, grande = false, className = '', title, testId, como = 'span', style, ...resto
}) {
  const Elemento = como;
  return (
    <Elemento
      className={`${AJUSTE_ETIQUETA_MARCA} ${grande ? FORMA_ETIQUETA_GRANDE : FORMA_ETIQUETA} text-white ${className}`.trim()}
      style={{ backgroundColor: colorDeMarca(color), ...(style || {}) }}
      title={title || tituloMarca(texto)}
      data-testid={testId}
      data-marca-paciente="1"
      {...resto}
    >
      {texto}
    </Elemento>
  );
}

/**
 * Las de una cita, una detrás de otra y pasando a la línea siguiente si no
 * caben (en el móvil, sin scroll de lado). Para el resumen de la cita y el
 * parte del día. Sin ninguna, nada.
 */
export default function EtiquetasMarcasPaciente({ marcas, testId = 'cita-marcas-paciente', className = '' }) {
  const lista = marcasDeLaCita(marcas);
  if (!lista.length) return null;
  return (
    <span
      className={`inline-flex flex-wrap items-center gap-1 min-w-0 max-w-full ${className}`.trim()}
      data-testid={testId}
      role="list"
      aria-label="Marcas de paciente"
    >
      {lista.map((m) => (
        <EtiquetaMarcaPaciente
          key={m.id || m.texto}
          texto={m.texto}
          color={m.color}
          role="listitem"
          testId={`${testId}-item`}
        />
      ))}
    </span>
  );
}
